// Lecturas con caché (TanStack Query). TODA clave de datos de una empresa empieza con
// ['emp', empresaId, …]: cambiar de empresa las borra de una vez (SesionContexto.elegirEmpresa).
// Las búsquedas van al servidor con debounce corto y paginadas; nunca se descarga la tabla completa.
import {
  keepPreviousData,
  useInfiniteQuery,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from './cliente';
import type {
  PagoNota,
  Canal,
  Categoria,
  ClienteEncontrado,
  ContextoComercial,
  Cumplimiento,
  Despacho,
  DocumentacionNota,
  DocumentoDte,
  DocumentoDteListado,
  EstadoDte,
  FichaEntidad,
  FichaProducto,
  FilaDisponibilidad,
  ListaPrecio,
  Marca,
  Nota,
  NotaListada,
  Presentacion,
  PrecioVigente,
  Previsualizacion,
  PrevisualizarNota,
  PoliticaCampo,
  ProductoEncontrado,
  PuntoCobro,
  TurnoCaja,
  TurnoListado,
  AuditoriaTurno,
  CandidatoCaja,
  ElegiblesPunto,
  ElegibleTurno,
  ExcepcionCierre,
  ExcepcionCierreDetalle,
  PoliticaCierre,
  SolicitudAnulacion,
  CuentaCompartida,
  SolicitudCambio,
  Ubicacion,
  UltimaCompra,
  Vendedor,
} from './contratos';

export const claves = {
  pagoNota: (e: string, id: string) => ['emp', e, 'pago-nota', id] as const,
  /** Todo lo de caja (turnos, puntos) cuelga de aquí: invalidar `caja(e)` refresca el mesón. */
  caja: (e: string) => ['emp', e, 'caja'] as const,
  turnoActual: (e: string) => ['emp', e, 'caja', 'turno-actual'] as const,
  turnos: (e: string, estado: string) => ['emp', e, 'caja', 'turnos', estado] as const,
  turno: (e: string, id: string) => ['emp', e, 'caja', 'turno', id] as const,
  puntos: (e: string) => ['emp', e, 'caja', 'puntos'] as const,
  /** 0130: elegibles, política y excepciones también cuelgan de caja(e). */
  elegiblesPunto: (e: string, p: string) => ['emp', e, 'caja', 'elegibles-punto', p] as const,
  elegiblesTurno: (e: string, t: string) => ['emp', e, 'caja', 'elegibles-turno', t] as const,
  candidatosCaja: (e: string) => ['emp', e, 'caja', 'candidatos'] as const,
  politicaCierre: (e: string, p: string) => ['emp', e, 'caja', 'politica', p] as const,
  excepciones: (e: string, filtro: string) => ['emp', e, 'caja', 'excepciones', filtro] as const,
  excepcion: (e: string, id: string) => ['emp', e, 'caja', 'excepcion', id] as const,
  auditoriaTurno: (e: string, t: string) => ['emp', e, 'caja', 'auditoria', t] as const,
  /** V4: solicitudes de anulación y cuentas compartidas de puesto. */
  solicitudesAnulacion: (e: string, estado: string) =>
    ['emp', e, 'caja', 'solicitudes-anulacion', estado] as const,
  cuentasCompartidas: (e: string) => ['emp', e, 'caja', 'cuentas-compartidas'] as const,
  ubicaciones: (e: string) => ['emp', e, 'ubicaciones'] as const,
  clientes: (e: string, t: string) => ['emp', e, 'clientes', t] as const,
  cliente: (e: string, id: string) => ['emp', e, 'cliente', id] as const,
  productos: (e: string, t: string) => ['emp', e, 'productos', t] as const,
  presentaciones: (e: string, id: string) => ['emp', e, 'presentaciones', id] as const,
  precio: (e: string, lista: string, pres: string) => ['emp', e, 'precio', lista, pres] as const,
  disponibilidad: (e: string, filtro: string) => ['emp', e, 'disponibilidad', filtro] as const,
  nota: (e: string, id: string) => ['emp', e, 'nota', id] as const,
  notas: (e: string, filtros: object) => ['emp', e, 'notas', filtros] as const,
  cumplimiento: (e: string, id: string) => ['emp', e, 'cumplimiento', id] as const,
  despachos: (e: string, notaId: string) => ['emp', e, 'despachos', notaId] as const,
  despacho: (e: string, id: string) => ['emp', e, 'despacho', id] as const,
  documentacion: (e: string, id: string) => ['emp', e, 'documentacion', id] as const,
  previsualizacion: (e: string, cuerpo: string) => ['emp', e, 'previsualizacion', cuerpo] as const,
  estadoDte: (e: string, id: string) => ['emp', e, 'dte-estado', id] as const,
  documentoDte: (e: string, id: string) => ['emp', e, 'dte', id] as const,
  documentosDte: (e: string, f: object) => ['emp', e, 'dte-lista', f] as const,
  vendedores: (e: string) => ['emp', e, 'vendedores'] as const,
  listas: (e: string) => ['emp', e, 'listas-precio'] as const,
  escalas: (e: string, precioId: string) => ['emp', e, 'escalas', precioId] as const,
  compras: (e: string, clienteId: string) => ['emp', e, 'ultimas-compras', clienteId] as const,
  contexto: (e: string, clienteId: string, canal: string) =>
    ['emp', e, 'contexto-comercial', clienteId, canal] as const,
  producto: (e: string, id: string) => ['emp', e, 'producto', id] as const,
  politicaFicha: (e: string, entidadId: string | null = null) =>
    ['emp', e, 'politica-ficha', entidadId] as const,
  solicitudes: (e: string, f: object) => ['emp', e, 'solicitudes-cambio', f] as const,
};

/** La ficha del producto: unidad base, presentaciones (RTU) y venta en múltiplos. */
export function useProducto(empresaId: string, productoId: string | null) {
  return useQuery({
    queryKey: claves.producto(empresaId, productoId ?? ''),
    queryFn: () => api.get<FichaProducto>(`/productos/${productoId}`),
    enabled: !!productoId,
    staleTime: 5 * 60_000,
  });
}

/**
 * Qué campos de la ficha aplica directo este usuario y cuáles van por solicitud. Con el cliente, lo
 * que a SU ficha le falta (dirección tributaria, teléfono, correo) sale directo (31-10).
 */
export function usePoliticaFicha(empresaId: string, entidadId: string | null = null) {
  return useQuery({
    queryKey: claves.politicaFicha(empresaId, entidadId),
    queryFn: () =>
      api.get<PoliticaCampo[]>('/entidades/ficha/politica', {
        query: { empresaId, ...(entidadId ? { entidadId } : {}) },
      }),
    staleTime: 5 * 60_000,
  });
}

/** Solicitudes de cambio de la ficha: de un cliente (ficha) o de la empresa (bandeja). */
export function useSolicitudesCambio(
  empresaId: string,
  f: { entidadId?: string; estado?: SolicitudCambio['estado'] },
  habilitado = true,
) {
  return useQuery({
    queryKey: claves.solicitudes(empresaId, f),
    queryFn: () =>
      api.get<SolicitudCambio[]>('/entidades/solicitudes-cambio', {
        query: { empresaId, entidadId: f.entidadId, estado: f.estado },
      }),
    enabled: habilitado,
    staleTime: 15_000,
  });
}

/** Debounce corto para búsquedas mientras se escribe. */
export function useDebounce<T>(valor: T, ms = 200): T {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

export function useUbicaciones(empresaId: string) {
  return useQuery({
    queryKey: claves.ubicaciones(empresaId),
    queryFn: () => api.get<Ubicacion[]>('/inventario/ubicaciones', { query: { empresaId } }),
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------- búsquedas paginadas (C-5, D-UI-2)

/** Tamaño de página de los buscadores. Se pide UNA fila más para saber si hay otra página. */
export const PAGINA_BUSQUEDA = 25;

export interface ResultadoPaginado<T> {
  filas: T[];
  hayMas: boolean;
  cargarMas: () => void;
  cargandoMas: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Búsqueda paginada en el servidor: cada página son `limite + 1` filas desde `desplazamiento`, y
 * «cargar más» pide la siguiente. Cambiar el texto cancela la búsqueda anterior (TanStack Query aborta
 * el `signal` de la consulta que quedó sin observadores).
 */
function usePaginado<T>(
  clave: readonly unknown[],
  ruta: string,
  query: Record<string, string | undefined>,
  habilitado: boolean,
): ResultadoPaginado<T> {
  const q = useInfiniteQuery({
    queryKey: clave,
    queryFn: ({ pageParam, signal }) =>
      api.get<T[]>(ruta, {
        query: { ...query, limite: PAGINA_BUSQUEDA + 1, desplazamiento: pageParam },
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (ultima, _todas, desde) =>
      ultima.length > PAGINA_BUSQUEDA ? desde + PAGINA_BUSQUEDA : undefined,
    enabled: habilitado,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const filas = useMemo(
    () => (habilitado ? (q.data?.pages ?? []).flatMap((p) => p.slice(0, PAGINA_BUSQUEDA)) : []),
    [q.data, habilitado],
  );
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = q;
  const cargarMas = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  return {
    filas,
    hayMas: habilitado && !!hasNextPage,
    cargarMas,
    cargandoMas: isFetchingNextPage,
    isFetching: q.isFetching,
    isError: q.isError,
    error: q.error,
  };
}

export type OrdenClientes = 'razon_social' | 'rut';
export function useBuscarClientes(
  empresaId: string,
  texto: string,
  orden: OrdenClientes = 'razon_social',
  rol: 'cliente' | 'proveedor' = 'cliente',
) {
  const t = texto.trim();
  return usePaginado<ClienteEncontrado>(
    [...claves.clientes(empresaId, t), orden, rol],
    '/entidades',
    { empresaId, rol, texto: t, orden },
    t.length >= 2,
  );
}

export function useFichaCliente(empresaId: string, entidadId: string | null) {
  return useQuery({
    queryKey: claves.cliente(empresaId, entidadId ?? ''),
    queryFn: () => api.get<FichaEntidad>(`/entidades/${entidadId}`),
    enabled: !!entidadId,
    staleTime: 60_000,
  });
}

export interface FiltroProductos {
  categoriaId?: string | undefined;
  marcaId?: string | undefined;
  orden?: 'nombre' | 'sku';
}
/**
 * Un código de barras (solo dígitos, 8 o más) se busca exacto; lo demás, por texto/SKU. Familia
 * (categoría) y marca son filtros del servidor: sin texto, basta con elegir una de ellas.
 */
export function useBuscarProductos(empresaId: string, texto: string, f: FiltroProductos = {}) {
  const t = texto.trim();
  const esCodigo = /^\d{8,}$/.test(t);
  return usePaginado<ProductoEncontrado>(
    [...claves.productos(empresaId, t), f],
    '/productos',
    {
      empresaId,
      estado: 'activo',
      ...(t ? (esCodigo ? { codigo: t } : { texto: t }) : {}),
      categoriaId: f.categoriaId,
      marcaId: f.marcaId,
      orden: f.orden ?? 'nombre',
    },
    t.length >= 2 || !!f.categoriaId || !!f.marcaId,
  );
}

export function useCategorias(habilitado = true) {
  return useQuery({
    queryKey: ['catalogo', 'categorias'],
    queryFn: () => api.get<Categoria[]>('/catalogo/categorias'),
    enabled: habilitado,
    staleTime: 10 * 60_000,
    retry: false,
  });
}
export function useMarcas(habilitado = true) {
  return useQuery({
    queryKey: ['catalogo', 'marcas'],
    queryFn: () => api.get<Marca[]>('/catalogo/marcas'),
    enabled: habilitado,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function usePresentaciones(empresaId: string, productoId: string | null) {
  return useQuery({
    queryKey: claves.presentaciones(empresaId, productoId ?? ''),
    queryFn: () => api.get<Presentacion[]>(`/productos/${productoId}/presentaciones`),
    enabled: !!productoId,
    staleTime: 5 * 60_000,
  });
}

/** Precio de lista VIGENTE de una presentación: referencia para el buscador, no el de la nota. */
export function usePrecioReferencia(
  empresaId: string,
  listaId: string | null,
  productoId: string | null,
  presentacionId: string | null,
) {
  return useQuery({
    queryKey: claves.precio(empresaId, listaId ?? '', presentacionId ?? ''),
    queryFn: async () => {
      try {
        return await api.get<PrecioVigente>('/precios/vigente', {
          query: { listaId: listaId!, productoId: productoId!, presentacionId: presentacionId! },
        });
      } catch (e) {
        // 404 sin_precio_vigente es un dato ("sin precio"), no una falla.
        if ((e as { status?: number }).status === 404) return null;
        throw e;
      }
    },
    enabled: !!listaId && !!productoId && !!presentacionId,
    staleTime: 60_000,
  });
}

/**
 * Disponibilidad (físico − reservado vigente) en UNIDAD BASE, por ubicación. Se pide por producto o
 * por ubicación; nunca "todo el inventario". Stock cambia rápido: caché corto.
 */
export function useDisponibilidad(
  empresaId: string,
  filtro: { productoId?: string | null; ubicacionId?: string | null },
) {
  const clave = `${filtro.productoId ?? ''}|${filtro.ubicacionId ?? ''}`;
  return useQuery({
    queryKey: claves.disponibilidad(empresaId, clave),
    queryFn: () =>
      api.get<FilaDisponibilidad[]>('/inventario/disponibilidad', {
        query: {
          empresaId,
          productoId: filtro.productoId ?? undefined,
          ubicacionId: filtro.ubicacionId ?? undefined,
        },
      }),
    enabled: !!(filtro.productoId || filtro.ubicacionId),
    staleTime: 10_000,
    // Lo que otro hace (venta, ajuste de bodega) se ve solo, sin recargar (2026-10-25).
    refetchInterval: 15_000,
  });
}

/**
 * La nota. Mientras espera la firma (EN_APROBACION) se consulta sola: cada 3 s si el vendedor está
 * esperando en línea, cada 10 s si la dejó pendiente. Al firmarse, la pantalla se actualiza sola.
 */
export function useNota(empresaId: string, notaId: string | null, esperandoEnLinea = false) {
  return useQuery({
    queryKey: claves.nota(empresaId, notaId ?? ''),
    queryFn: () => api.get<Nota>(`/ventas/notas/${notaId}`),
    enabled: !!notaId,
    staleTime: 5_000,
    refetchInterval: (q) =>
      q.state.data?.estado === 'EN_APROBACION' ? (esperandoEnLinea ? 3_000 : 10_000) : false,
  });
}

export interface FiltrosNotas {
  desde?: string | undefined;
  hasta?: string | undefined;
  estado?: string | undefined;
  canal?: string | undefined;
  numero?: string | undefined;
  clienteEntidadId?: string | undefined;
  /** Prefijo del RUT del cliente (solo dígitos, sin DV). */
  rut?: string | undefined;
  /** FACTURA / BOLETA: el documento que pidió la venta. */
  documento?: string | undefined;
  /** Los pedidos de un vendedor (docs/108 B7). */
  vendedorId?: string | undefined;
  entrega?: string | undefined;
  /** 'false': solo los pedidos (sin las ventas directas); 'true': solo las ventas directas. */
  ventaDirecta?: 'true' | 'false' | undefined;
  /** Solo las que aún no tienen boleta/factura. */
  porDocumentar?: 'true' | undefined;
  pagina: number;
}
export const TAMANO_PAGINA = 50;
export function useNotas(
  empresaId: string,
  f: FiltrosNotas,
  habilitado = true,
  refrescarCada: number | false = false,
) {
  return useQuery({
    queryKey: claves.notas(empresaId, f),
    queryFn: () =>
      api.get<{ items: NotaListada[]; pagina: number; tamano: number }>('/ventas/notas', {
        query: { empresaId, ...f, tamano: TAMANO_PAGINA },
      }),
    enabled: habilitado,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
    refetchInterval: refrescarCada,
  });
}

export function useCumplimiento(empresaId: string, notaId: string | null) {
  return useQuery({
    queryKey: claves.cumplimiento(empresaId, notaId ?? ''),
    queryFn: () => api.get<Cumplimiento>(`/ventas/notas/${notaId}/cumplimiento`),
    enabled: !!notaId,
    staleTime: 5_000,
  });
}

export function useDespachosDeNota(empresaId: string, notaId: string | null) {
  return useQuery({
    queryKey: claves.despachos(empresaId, notaId ?? ''),
    queryFn: () =>
      api.get<{ items: Despacho[] }>('/ventas/despachos', {
        query: { empresaId, notaId: notaId! },
      }),
    enabled: !!notaId,
    staleTime: 5_000,
  });
}

/** El detalle de cada despacho de la nota (sus líneas), para el historial #1, #2… */
export function useDetallesDespacho(empresaId: string, ids: string[]) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: claves.despacho(empresaId, id),
      queryFn: () => api.get<Despacho>(`/ventas/despachos/${id}`),
      staleTime: 10_000,
    })),
  });
}

export function useDocumentacion(empresaId: string, notaId: string | null, habilitado = true) {
  return useQuery({
    queryKey: claves.documentacion(empresaId, notaId ?? ''),
    queryFn: () => api.get<DocumentacionNota>(`/ventas/notas/${notaId}/documentacion`),
    enabled: !!notaId && habilitado,
    staleTime: 10_000,
    retry: false,
  });
}

// ---------------------------------------------------------------- previsualización (C-4, D-UI-1)

export type EstadoCalculo = 'incompleto' | 'cambio' | 'calculando' | 'actualizado' | 'error';

/**
 * Previsualiza la nota en el backend SIN guardar nada: cada cambio (cliente, producto, cantidad,
 * presentación, flete, condición) pide un cálculo nuevo tras una pausa corta; mientras tanto se ve la
 * respuesta anterior marcada como «cambió». `recalcularAhora` (F8) salta la pausa. El pedido que quedó
 * viejo se cancela. El frontend no calcula: pinta lo que responde el backend.
 */
export function usePrevisualizacion(
  empresaId: string,
  cuerpo: PrevisualizarNota | null,
  pausa = 350,
) {
  const clave = cuerpo ? JSON.stringify(cuerpo) : '';
  const [pedida, setPedida] = useState(clave);
  useEffect(() => {
    if (clave === pedida) return;
    const t = setTimeout(() => setPedida(clave), pausa);
    return () => clearTimeout(t);
  }, [clave, pedida, pausa]);
  const q = useQuery({
    queryKey: claves.previsualizacion(empresaId, pedida),
    queryFn: ({ signal }) =>
      api.post<Previsualizacion>(
        '/ventas/notas/previsualizar',
        JSON.parse(pedida) as PrevisualizarNota,
        { signal },
      ),
    enabled: !!pedida,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    gcTime: 60_000,
    retry: false,
  });
  const qc = useQueryClient();
  const recalcularAhora = useCallback(async () => {
    if (!clave) return;
    if (clave !== pedida) setPedida(clave);
    else await qc.invalidateQueries({ queryKey: claves.previsualizacion(empresaId, clave) });
  }, [clave, pedida, qc, empresaId]);
  const estado: EstadoCalculo = !clave
    ? 'incompleto'
    : pedida !== clave
      ? 'cambio'
      : q.isFetching
        ? 'calculando'
        : q.isError
          ? 'error'
          : q.data && !q.isPlaceholderData
            ? 'actualizado'
            : 'calculando';
  return {
    /** La última respuesta del backend (puede ser de una versión anterior: ver `estado`). */
    datos: clave ? q.data : undefined,
    estado,
    error: q.isError ? q.error : null,
    recalcularAhora,
  };
}

// ---------------------------------------------------------------- DTE (agnóstico del motor, C-6)

export function useEstadoDte(empresaId: string, documentoId: string | null, habilitado = true) {
  return useQuery({
    queryKey: claves.estadoDte(empresaId, documentoId ?? ''),
    queryFn: () => api.get<EstadoDte>(`/dte/documentos/${documentoId}/estado`),
    enabled: !!documentoId && habilitado,
    staleTime: 10_000,
    retry: false,
  });
}

export function useDocumentoDte(empresaId: string, documentoId: string | null, habilitado = true) {
  return useQuery({
    queryKey: claves.documentoDte(empresaId, documentoId ?? ''),
    queryFn: () => api.get<DocumentoDte>(`/dte/documentos/${documentoId}`),
    enabled: !!documentoId && habilitado,
    staleTime: 10_000,
    retry: false,
  });
}

/** Documentos tributarios por folio y tipo (consulta documental): su origen es la nota. */
export function useDocumentosDte(
  empresaId: string,
  f: { folio?: string | undefined; tipoDte?: string | undefined },
  habilitado: boolean,
) {
  return useQuery({
    queryKey: claves.documentosDte(empresaId, f),
    queryFn: () =>
      api.get<DocumentoDteListado[]>('/dte/documentos', {
        query: { empresaId, folio: f.folio, tipoDte: f.tipoDte },
      }),
    enabled: habilitado && !!f.folio,
    staleTime: 10_000,
    retry: false,
  });
}

// ---------------------------------------------------------------- puesto comercial (C2.1, slice 2A.1)

/** Vendedores de la empresa (maestro `entidades.vendedor`): para mostrar el nombre del vendedor. */
export function useVendedores(empresaId: string) {
  return useQuery({
    queryKey: claves.vendedores(empresaId),
    queryFn: async () =>
      (await api.get<Vendedor[]>('/vendedores')).filter((v) => v.empresa_id === empresaId),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

/** Listas de precio autorizadas para la empresa (no hay autorización por usuario: GAP 5). */
export function useListasPrecio(empresaId: string) {
  return useQuery({
    queryKey: claves.listas(empresaId),
    queryFn: () => api.get<ListaPrecio[]>('/precios/listas'),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export interface TramoEscala {
  id: string;
  cantidad_desde: string;
  cantidad_hasta: string | null;
  /** Precio por UNIDAD BASE del tramo. */
  valor: string;
}
/** Tramos de escala de un precio (para el panel Producto). */
export function useEscalas(empresaId: string, precioId: string | null) {
  return useQuery({
    queryKey: claves.escalas(empresaId, precioId ?? ''),
    queryFn: () => api.get<TramoEscala[]>(`/precios/${precioId}/escalas`),
    enabled: !!precioId,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Cuántas líneas compradas muestra el panel. */
export const LIMITE_ULTIMAS_COMPRAS = 30;
/**
 * Últimas compras del cliente en la empresa (2A.1 §8): UNA lectura del endpoint de historial, línea por
 * línea, de notas comprometidas. El backend filtra por empresa y alcance.
 */
export function useUltimasCompras(empresaId: string, clienteId: string | null) {
  return useQuery({
    queryKey: claves.compras(empresaId, clienteId ?? ''),
    queryFn: ({ signal }) =>
      api.get<UltimaCompra[]>(`/ventas/clientes/${clienteId}/ultimas-compras`, {
        query: { empresaId, limite: LIMITE_ULTIMAS_COMPRAS },
        signal,
      }),
    enabled: !!clienteId,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Contexto comercial del cliente (2A.1 §11): valores por defecto, lo permitido y los permisos
 * EFECTIVOS del actor para cambiar vendedor, lista y condición. La interfaz no decide por rol.
 */
export function useContextoComercial(empresaId: string, clienteId: string | null, canal: Canal) {
  return useQuery({
    queryKey: claves.contexto(empresaId, clienteId ?? '', canal),
    queryFn: ({ signal }) =>
      api.get<ContextoComercial>('/ventas/contexto-comercial', {
        query: { empresaId, clienteId: clienteId!, canal },
        signal,
      }),
    enabled: !!clienteId,
    staleTime: 60_000,
    retry: false,
  });
}

export interface Comuna {
  codigo: string;
  nombre: string;
}
/** Nombres de comuna (catálogo fijo de 346): se piden una vez por sesión. */
export function useComunas() {
  return useQuery({
    queryKey: ['geo', 'comunas'],
    queryFn: () => api.get<Comuna[]>('/geo/comunas'),
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Precio de LISTA (sin escala) de varias presentaciones a la vez, con la MISMA clave de caché que
 * {@link usePrecioReferencia}: la grilla y el pie de totales comparten las respuestas.
 */
export function usePreciosLista(
  empresaId: string,
  listaId: string | null,
  lineas: ReadonlyArray<{ productoId: string; presentacionId: string }>,
) {
  // Una consulta por presentación (dos líneas de la misma presentación comparten respuesta).
  const unicas = [...new Map(lineas.map((l) => [l.presentacionId, l])).values()];
  const r = useQueries({
    queries: unicas.map((l) => ({
      queryKey: claves.precio(empresaId, listaId ?? '', l.presentacionId),
      queryFn: async () => {
        try {
          return await api.get<PrecioVigente>('/precios/vigente', {
            query: {
              listaId: listaId!,
              productoId: l.productoId,
              presentacionId: l.presentacionId,
            },
          });
        } catch (e) {
          if ((e as { status?: number }).status === 404) return null;
          throw e;
        }
      },
      enabled: !!listaId,
      staleTime: 60_000,
    })),
  });
  return lineas.map((l) => r[unicas.findIndex((u) => u.presentacionId === l.presentacionId)]);
}

/** Mesón · fase 1: estado de pago de la nota (recibos de caja y su aplicación al documento). */
export function usePagoNota(empresaId: string, notaId: string | null, habilitado = true) {
  return useQuery({
    queryKey: claves.pagoNota(empresaId, notaId ?? ''),
    queryFn: () => api.get<PagoNota>(`/caja/notas/${notaId}/pago`),
    enabled: !!notaId && habilitado,
    staleTime: 15_000,
  });
}

// ---------------------------------------------------------------- mesón · fase 2a: turno de caja

/** Mi turno de caja abierto (o null). Cierre ciego: no trae lo esperado. */
export function useTurnoActual(empresaId: string, habilitado = true) {
  return useQuery({
    queryKey: claves.turnoActual(empresaId),
    queryFn: async () =>
      (await api.get<{ turno: TurnoCaja | null }>(`/caja/turnos/actual?empresaId=${empresaId}`))
        .turno,
    enabled: habilitado,
    staleTime: 10_000,
  });
}

export function usePuntosCobro(empresaId: string, habilitado = true) {
  return useQuery({
    queryKey: claves.puntos(empresaId),
    queryFn: () => api.get<PuntoCobro[]>(`/caja/puntos?empresaId=${empresaId}`),
    enabled: habilitado,
    staleTime: 10_000,
  });
}

export function useTurnos(empresaId: string, estado: string, habilitado = true) {
  return useQuery({
    queryKey: claves.turnos(empresaId, estado),
    queryFn: () =>
      api.get<TurnoListado[]>(
        `/caja/turnos?empresaId=${empresaId}${estado ? `&estado=${estado}` : ''}`,
      ),
    enabled: habilitado,
    staleTime: 10_000,
  });
}

export function useTurno(empresaId: string, turnoId: string | null) {
  return useQuery({
    queryKey: claves.turno(empresaId, turnoId ?? ''),
    queryFn: () => api.get<TurnoCaja>(`/caja/turnos/${turnoId}`),
    enabled: !!turnoId,
    staleTime: 5_000,
  });
}

// ---------------------------------------------------------------- 0130: turno compartido

/** Elegibles de una caja (la responsable, al abrir; administración, con la historia). */
export function useElegiblesPunto(empresaId: string, puntoId: string | null, habilitado = true) {
  return useQuery({
    queryKey: claves.elegiblesPunto(empresaId, puntoId ?? ''),
    queryFn: () => api.get<ElegiblesPunto>(`/caja/puntos/${puntoId}/elegibles`),
    enabled: !!puntoId && habilitado,
    staleTime: 10_000,
  });
}

/** Elegibles de la caja del turno, con su estado (para que la responsable los habilite). */
export function useElegiblesTurno(empresaId: string, turnoId: string, habilitado = true) {
  return useQuery({
    queryKey: claves.elegiblesTurno(empresaId, turnoId),
    queryFn: () => api.get<ElegibleTurno[]>(`/caja/turnos/${turnoId}/elegibles`),
    enabled: habilitado,
    staleTime: 10_000,
  });
}

export function useCandidatosCaja(empresaId: string, habilitado = true) {
  return useQuery({
    queryKey: claves.candidatosCaja(empresaId),
    queryFn: () => api.get<CandidatoCaja[]>(`/caja/operadores/candidatos?empresaId=${empresaId}`),
    enabled: habilitado,
    staleTime: 30_000,
  });
}

export function usePoliticaCierre(empresaId: string, puntoId: string | null) {
  return useQuery({
    queryKey: claves.politicaCierre(empresaId, puntoId ?? ''),
    queryFn: () =>
      api.get<{
        vigente: PoliticaCierre | null;
        versiones: PoliticaCierre[];
        /** PILOT_DEFAULT: con qué se prellena una política nueva (vienen del servidor, no de aquí). */
        pilotoPorDefecto: PoliticaCierre;
      }>(`/caja/puntos/${puntoId}/politica-cierre`),
    enabled: !!puntoId,
    staleTime: 10_000,
  });
}

/** Cola de excepciones de cierre: abiertas por nivel, o las resueltas. */
export function useExcepcionesCierre(
  empresaId: string,
  filtro: {
    estado: 'ABIERTA' | 'RESUELTA';
    nivel?: 1 | 2;
    cola?: 'PRINCIPAL' | 'ALTERNO' | 'OPEN_REQUIRES_INDEPENDENT_REVIEW';
  },
  habilitado = true,
) {
  const q = `estado=${filtro.estado}${filtro.nivel ? `&nivel=${filtro.nivel}` : ''}${filtro.cola ? `&cola=${filtro.cola}` : ''}`;
  return useQuery({
    queryKey: claves.excepciones(empresaId, q),
    queryFn: () => api.get<ExcepcionCierre[]>(`/caja/excepciones?empresaId=${empresaId}&${q}`),
    enabled: habilitado,
    staleTime: 10_000,
  });
}

export function useExcepcionCierre(empresaId: string, excepcionId: string | null) {
  return useQuery({
    queryKey: claves.excepcion(empresaId, excepcionId ?? ''),
    queryFn: () => api.get<ExcepcionCierreDetalle>(`/caja/excepciones/${excepcionId}`),
    enabled: !!excepcionId,
    staleTime: 5_000,
  });
}

/** V4 · las solicitudes de anulación (tesorería las ejecuta o rechaza). */
export function useSolicitudesAnulacion(
  empresaId: string,
  estado: 'PENDIENTE' | 'EJECUTADA' | 'RECHAZADA' = 'PENDIENTE',
  habilitado = true,
) {
  return useQuery({
    queryKey: claves.solicitudesAnulacion(empresaId, estado),
    queryFn: () =>
      api.get<SolicitudAnulacion[]>(
        `/caja/solicitudes-anulacion?empresaId=${empresaId}&estado=${estado}`,
      ),
    enabled: habilitado,
    staleTime: 10_000,
    retry: false,
  });
}

/** V4 · la lista de cuentas compartidas de puesto (legado). */
export function useCuentasCompartidas(empresaId: string, habilitado = true) {
  return useQuery({
    queryKey: claves.cuentasCompartidas(empresaId),
    queryFn: () => api.get<CuentaCompartida[]>(`/caja/cuentas-compartidas?empresaId=${empresaId}`),
    enabled: habilitado,
    staleTime: 10_000,
    retry: false,
  });
}

/** Quién hizo cada operación del turno, con qué sesión (equipo e IP) y cuándo. */
export function useAuditoriaTurno(empresaId: string, turnoId: string | null) {
  return useQuery({
    queryKey: claves.auditoriaTurno(empresaId, turnoId ?? ''),
    queryFn: () => api.get<AuditoriaTurno>(`/caja/turnos/${turnoId}/auditoria`),
    enabled: !!turnoId,
    staleTime: 5_000,
  });
}

/** Mesón: el cliente «consumidor final» (RUT 66.666.666-6, CLIENTE BOLETAS en Random). */
export function useConsumidorFinal(empresaId: string, habilitado: boolean) {
  return useQuery({
    queryKey: ['emp', empresaId, 'consumidor-final'],
    queryFn: async () =>
      (
        await api.get<ClienteEncontrado[]>('/entidades', {
          query: { empresaId, rol: 'cliente', texto: '66666666', limite: '5' },
        })
      ).find((c) => c.rut === '66666666')?.id ?? null,
    enabled: habilitado,
    staleTime: 600_000,
  });
}

/** El número que tendrá la próxima nota (se ve al abrir una nueva; se asigna al guardar). */
export function useProximoNumeroNota(empresaId: string, habilitado: boolean) {
  return useQuery({
    queryKey: ['emp', empresaId, 'nota-proximo-numero'],
    queryFn: () =>
      api.get<{ numero: string | null; numeroVisible: string | null }>(
        '/ventas/notas/proximo-numero',
        { query: { empresaId } },
      ),
    enabled: habilitado,
    refetchInterval: habilitado ? 15_000 : false,
  });
}

export interface ProximoFolio {
  folio: number | null;
  motivo: 'SIN_EMISOR' | 'TIPO_NO_HABILITADO' | 'LO_ASIGNA_EL_EMISOR' | 'SIN_CAF' | null;
}

/** Punto de venta: el folio que llevaría la boleta (39) o la factura (33). Solo mostrar. */
export function useProximoFolio(empresaId: string, tipoDte: number, habilitado: boolean) {
  return useQuery({
    queryKey: ['emp', empresaId, 'dte-proximo-folio', tipoDte],
    queryFn: () =>
      api.get<ProximoFolio>('/dte/folios/proximo', {
        query: { empresaId, tipoDte: String(tipoDte) },
      }),
    enabled: habilitado,
    refetchInterval: habilitado ? 15_000 : false,
  });
}
